import { Button } from "@/components/ui/button";
import { Printer, ConciergeBell } from "lucide-react";

// Status chip metadata — IDs match the strings returned by
// ``getTableStatus`` in Restaurant.jsx (free / ordering / mixed / pending).
// The colour mapping mirrors the existing table-card border palette so
// the chip swatch teaches the operator the legend at a glance.
const STATUS_FILTERS = [
  { id: "free",     label: "ΕΛΕΥΘΕΡΑ",       dot: "#10B981" },
  { id: "ordering", label: "ΣΕ ΠΑΡΑΓΓΕΛΙΑ",  dot: "#F97316" },
  { id: "pending",  label: "ΣΕ ΑΝΑΜΟΝΗ",     dot: "#F59E0B" },
  { id: "mixed",    label: "ΜΕΡΙΚΩΣ",        dot: "#EF4444" },
];

const BTN_HEIGHT = "h-14"; // double-height per spec ("ΔΙΠΛΑΣΙΟ ΥΨΟΣ")

/**
 * Single-row filter bar embedded in the Restaurant tables header.
 *
 * Layout (left → right):
 *   [ΟΛΑ] [section…] [section…]   …flex spacer…   [status…]×4   [Ανοιχτά Τραπέζια]
 *
 * • Sections start from the LEFT, statuses start from the RIGHT.
 * • The leading «ΟΛΑ» button is BOTH the «no section filter» indicator
 *   AND the master reset — clicking it clears any active section radio
 *   and every active status chip in one go (per user spec, replacing
 *   the older «Καθαρισμός» button).
 * • Each button is ``h-14`` so the bar reads at a glance from across
 *   the room on touchscreens, double the previous chip height.
 * • The «Ανοιχτά Τραπέζια» action sits at the far right so it doesn't
 *   compete with the filter chips for attention.
 */
export function TableGridFilters({
  sections,
  tables,
  activeSectionId,
  setActiveSectionId,
  activeStatuses,
  setActiveStatuses,
  getTableStatus,
  onPrintOpenTables,
  onOpenServing,
}) {
  // Status counts are scoped to the currently selected section so the
  // operator sees relevant numbers when zoomed into a zone.
  const sectionScoped = activeSectionId
    ? tables.filter((t) => t.section_id === activeSectionId)
    : tables;
  const countsByStatus = sectionScoped.reduce((acc, t) => {
    const s = getTableStatus(t);
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});
  const totalAll = tables.length;

  const toggleStatus = (id) => {
    setActiveStatuses((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id],
    );
  };
  const togglePick = (sid) => {
    setActiveSectionId((cur) => (cur === sid ? null : sid));
  };
  const resetAll = () => {
    setActiveSectionId(null);
    setActiveStatuses([]);
  };
  const allActive = activeSectionId === null && activeStatuses.length === 0;

  return (
    <div
      className="px-4 py-2 border-b-2 border-slate-300 bg-grid flex items-center gap-2 flex-nowrap"
      data-testid="tables-filter-bar"
    >
      <div className="flex items-center gap-2 overflow-x-auto scroll-thin flex-1 min-w-0">
      {/* «ΟΛΑ» — master reset + section-all radio */}
      <button
        type="button"
        onClick={resetAll}
        data-testid="filter-section-all"
        className={`${BTN_HEIGHT} px-4 rounded-lg text-base font-bold border-2 transition-all ${
          allActive
            ? "bg-slate-900 text-white border-slate-900 shadow-md"
            : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"
        }`}
      >
        ΟΛΑ
        <span className="ml-2 text-xs opacity-80 font-mono">({totalAll})</span>
      </button>

      {/* Section radio (LEFT side) */}
      {sections.map((s) => {
        const active = activeSectionId === s.id;
        const count = tables.filter((t) => t.section_id === s.id).length;
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => togglePick(s.id)}
            data-testid={`filter-section-${s.id}`}
            className={`${BTN_HEIGHT} px-4 rounded-lg text-base font-bold border-2 transition-all ${
              active ? "shadow-md" : "hover:brightness-95"
            }`}
            style={{
              background: active ? s.color : `${s.color}25`,
              borderColor: s.color,
              color: active ? "#fff" : "#1e293b",
            }}
            title={s.name}
          >
            {s.name}
            <span
              className={`ml-2 text-xs font-mono ${active ? "opacity-90" : "opacity-70"}`}
            >
              ({count})
            </span>
          </button>
        );
      })}

      {/* Flex spacer pushes the status group to the right edge */}
      <div className="flex-1" />

      {/* Status multi-select (RIGHT side) */}
      {STATUS_FILTERS.map((sf) => {
        const active = activeStatuses.includes(sf.id);
        const count = countsByStatus[sf.id] || 0;
        return (
          <button
            key={sf.id}
            type="button"
            onClick={() => toggleStatus(sf.id)}
            data-testid={`filter-status-${sf.id}`}
            className={`${BTN_HEIGHT} px-3 rounded-lg text-sm font-bold border-2 transition-all flex items-center gap-2 ${
              active ? "shadow-md" : "opacity-80 hover:opacity-100"
            }`}
            style={{
              background: active ? `${sf.dot}25` : "#ffffff",
              borderColor: sf.dot,
              color: "#1e293b",
            }}
          >
            <span
              className="w-3 h-3 rounded-full inline-block"
              style={{ background: sf.dot }}
            />
            {sf.label}
            <span className="text-xs font-mono opacity-70">({count})</span>
          </button>
        );
      })}
      </div>

      {/* Ενέργειες — καρφωμένες δεξιά, πάντα σε μία σειρά */}
      <div className="flex items-center gap-2 shrink-0 ml-1">
      {onOpenServing && (
        <Button
          variant="outline"
          onClick={onOpenServing}
          data-testid="open-serving-btn"
          className={`${BTN_HEIGHT} border-2 border-indigo-300 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 px-3 whitespace-nowrap`}
        >
          <ConciergeBell size={16} className="mr-1.5 shrink-0" />
          <span className="text-sm font-bold">Σερβίρισμα</span>
        </Button>
      )}

      {onPrintOpenTables && (
        <Button
          variant="outline"
          onClick={onPrintOpenTables}
          data-testid="print-open-tables-btn"
          className={`${BTN_HEIGHT} border-2 px-3 whitespace-nowrap leading-tight`}
        >
          <Printer size={14} className="mr-1.5 shrink-0" />
          <span className="text-xs font-bold text-left">
            Έλεγχος Ανοιχτών<br />Τραπεζιών
          </span>
        </Button>
      )}
      </div>
    </div>
  );
}
